import React, { useMemo, useState } from 'react';
import { Link, router } from '@inertiajs/react';

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

const CREDIT_HOUR_OPTIONS = [1, 2, 3, 4, 5];
const POLL_MIN_OPTIONS = 2;
const POLL_MAX_OPTIONS = 6;

const TIME_ZONE_OPTIONS = [
  { value: 'America/New_York',    label: 'Eastern (New York)' },
  { value: 'America/Chicago',     label: 'Central (Chicago)' },
  { value: 'America/Denver',      label: 'Mountain (Denver)' },
  { value: 'America/Phoenix',     label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage',   label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (Honolulu)' },
];

const pad = (n) => String(n).padStart(2, '0');

function splitStartsAt(value) {
  if (!value) return { date: '', time: '' };
  const [date = '', timePart = ''] = String(value).split('T');
  return { date, time: timePart.slice(0, 5) };
}

function addHoursLocal(date, time, hours) {
  if (!date || !time || !hours) return '';
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const local = new Date(y, m - 1, d, hh, mm);
  local.setHours(local.getHours() + Number(hours));
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}T${pad(local.getHours())}:${pad(local.getMinutes())}`;
}

function formatPreview(value) {
  if (!value) return '—';
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const dt = new Date(y, m - 1, d, hh, mm);
  return dt.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// A per-section blank state matching what the controller's blank_section_form emits.
function makeBlankSection(index, title = `Session ${index + 1}`) {
  return {
    id: null,
    title,
    description: '',
    position: index,
    // First session shows the instructor bio slide by default; later ones off.
    show_bio_slide: index === 0,
    slide_deck: { attached: false },
    slide_deck_file: null,
    polls: [makeBlankPoll(0), makeBlankPoll(1)],
    questions: [{ id: null, prompt: '', position: 0 }, { id: null, prompt: '', position: 1 }],
    _destroy: false,
  };
}

function makeBlankPoll(index) {
  return {
    id: null,
    question: '',
    position: index,
    options: [
      { id: null, body: '', position: 0 },
      { id: null, body: '', position: 1 },
    ],
  };
}

function normalizeSections(sections) {
  const list = (sections && sections.length > 0)
    ? sections
    : [{ id: null, title: 'Session 1', description: '', position: 0, show_bio_slide: true, slide_deck: { attached: false }, polls: [], questions: [] }];
  return list.map((s, idx) => ({
    id: s.id ?? null,
    title: s.title ?? `Session ${idx + 1}`,
    description: s.description ?? '',
    position: s.position ?? idx,
    show_bio_slide: s.show_bio_slide ?? (idx === 0),
    slide_deck: s.slide_deck ?? { attached: false },
    slide_deck_file: null,
    polls: normalizePolls(s.polls),
    questions: normalizeQuestions(s.questions),
    _destroy: false,
  }));
}

function normalizeQuestions(questions) {
  const base = Array.from({ length: 2 }, (_, i) => questions?.[i] || null);
  return base.map((q, idx) => ({
    id: q?.id ?? null,
    prompt: q?.prompt ?? '',
    position: q?.position ?? idx,
  }));
}

function normalizePolls(polls) {
  const base = Array.from({ length: 2 }, (_, i) => polls?.[i] || null);
  return base.map((p, idx) => {
    const options = p?.options?.length >= POLL_MIN_OPTIONS
      ? p.options.map((o) => ({ id: o.id ?? null, body: o.body ?? '', position: o.position ?? 0 }))
      : [
          { id: null, body: '', position: 0 },
          { id: null, body: '', position: 1 },
        ];
    return {
      id: p?.id ?? null,
      question: p?.question ?? '',
      position: p?.position ?? idx,
      options,
    };
  });
}

// Serialize the sections array into the nested params Rails expects. Multipart
// is required whenever any section has a new slide_deck file.
function anySectionHasFile(sections) {
  return sections.some((s) => s.slide_deck_file);
}

function appendSectionsToFormData(fd, sections) {
  sections.forEach((s, si) => {
    const base = `webinar[sections_attributes][${si}]`;
    if (s.id) fd.append(`${base}[id]`, String(s.id));
    fd.append(`${base}[title]`, s.title || '');
    fd.append(`${base}[description]`, s.description || '');
    fd.append(`${base}[position]`, String(si));
    fd.append(`${base}[show_bio_slide]`, s.show_bio_slide ? '1' : '0');
    if (s._destroy && s.id) fd.append(`${base}[_destroy]`, '1');
    if (s.slide_deck_file) fd.append(`${base}[slide_deck]`, s.slide_deck_file);
    s.polls.forEach((p, pi) => {
      const pbase = `${base}[polls_attributes][${pi}]`;
      if (p.id) fd.append(`${pbase}[id]`, String(p.id));
      fd.append(`${pbase}[question]`, p.question || '');
      fd.append(`${pbase}[position]`, String(pi));
      p.options.forEach((o, oi) => {
        const obase = `${pbase}[options_attributes][${oi}]`;
        if (o.id) fd.append(`${obase}[id]`, String(o.id));
        fd.append(`${obase}[body]`, o.body || '');
        fd.append(`${obase}[position]`, String(oi));
        if (o._destroy) fd.append(`${obase}[_destroy]`, '1');
      });
    });
    s.questions.forEach((q, qi) => {
      const qbase = `${base}[questions_attributes][${qi}]`;
      if (q.id) fd.append(`${qbase}[id]`, String(q.id));
      fd.append(`${qbase}[prompt]`, q.prompt || '');
      fd.append(`${qbase}[position]`, String(qi));
    });
  });
}

function sectionsJsonPayload(sections) {
  return sections.map((s, si) => ({
    id: s.id || undefined,
    title: s.title,
    description: s.description,
    position: si,
    show_bio_slide: !!s.show_bio_slide,
    _destroy: s._destroy && s.id ? true : undefined,
    polls_attributes: s.polls.map((p, pi) => ({
      id: p.id || undefined,
      question: p.question,
      position: pi,
      options_attributes: p.options.map((o, oi) => ({
        id: o.id || undefined,
        body: o.body,
        position: oi,
        _destroy: o._destroy ? true : undefined,
      })),
    })),
    questions_attributes: s.questions.map((q, qi) => ({
      id: q.id || undefined,
      prompt: q.prompt,
      position: qi,
    })),
  }));
}

function SectionEditor({ section, si, updateSection, removeSection, canRemove }) {
  const updateField = (field) => (e) => updateSection(si, { ...section, [field]: e.target.value });

  const updatePoll = (pi, poll) => {
    updateSection(si, {
      ...section,
      polls: section.polls.map((p, i) => (i === pi ? poll : p)),
    });
  };
  const addPoll = () => {
    updateSection(si, { ...section, polls: [...section.polls, makeBlankPoll(section.polls.length)] });
  };
  const removePoll = (pi) => {
    const target = section.polls[pi];
    if (target.id) {
      updateSection(si, { ...section, polls: section.polls.map((p, i) => (i === pi ? { ...p, _destroy: true } : p)) });
    } else {
      updateSection(si, { ...section, polls: section.polls.filter((_, i) => i !== pi) });
    }
  };
  const updateOption = (pi, oi, body) => {
    updatePoll(pi, {
      ...section.polls[pi],
      options: section.polls[pi].options.map((o, j) => (j === oi ? { ...o, body } : o)),
    });
  };
  const addOption = (pi) => {
    const p = section.polls[pi];
    if (p.options.length >= POLL_MAX_OPTIONS) return;
    updatePoll(pi, { ...p, options: [...p.options, { id: null, body: '', position: p.options.length }] });
  };
  const removeOption = (pi, oi) => {
    const p = section.polls[pi];
    if (p.options.length <= POLL_MIN_OPTIONS) return;
    const target = p.options[oi];
    if (target.id) {
      updatePoll(pi, { ...p, options: p.options.map((o, j) => (j === oi ? { ...o, _destroy: true } : o)) });
    } else {
      updatePoll(pi, { ...p, options: p.options.filter((_, j) => j !== oi) });
    }
  };

  const updateQuestion = (qi, prompt) => {
    updateSection(si, {
      ...section,
      questions: section.questions.map((q, i) => (i === qi ? { ...q, prompt } : q)),
    });
  };

  const primaryAttached = section.slide_deck?.attached && !section.slide_deck_file;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <label className={labelClass}>
            Session title <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={section.title}
            onChange={updateField('title')}
            required
            placeholder="e.g. Hour 1: Introduction"
            className={inputClass}
          />
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={() => removeSection(si)}
            className="mt-6 shrink-0 rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50"
          >
            Remove session
          </button>
        )}
      </div>

      <div className="mt-3">
        <label className={labelClass}>Notes (optional)</label>
        <textarea
          value={section.description}
          onChange={updateField('description')}
          rows={2}
          placeholder="Instructor notes about this session"
          className={inputClass}
        />
      </div>

      <div className="mt-4">
        <label className={labelClass}>Session PDF <span className="text-gray-400 font-normal">(optional)</span></label>
        {primaryAttached && (
          <div className="mb-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
            <p className="font-medium text-gray-900 truncate">{section.slide_deck.filename}</p>
            <p className="text-gray-500">{fmtBytes(section.slide_deck.byte_size)} · attached</p>
          </div>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => updateSection(si, { ...section, slide_deck_file: e.target.files?.[0] || null })}
          className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
        />
        {section.slide_deck_file && (
          <p className="mt-1 text-xs text-gray-500">
            New: {section.slide_deck_file.name} ({fmtBytes(section.slide_deck_file.size)})
          </p>
        )}
      </div>

      <div className="mt-3">
        <label className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={!!section.show_bio_slide}
            onChange={(e) => updateSection(si, { ...section, show_bio_slide: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>Show instructor bio slide at the start of this session</span>
        </label>
        <p className="mt-1 text-xs text-gray-500 pl-6">
          Only takes effect if the assigned instructor has uploaded a bio slide.
        </p>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Polls</h4>
          <button
            type="button"
            onClick={addPoll}
            className="rounded-md border border-dashed border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
          >
            + Add poll
          </button>
        </div>
        <div className="space-y-3">
          {section.polls.map((poll, pi) => {
            if (poll._destroy) return null;
            const visibleOptions = poll.options.filter((o) => !o._destroy);
            return (
              <div key={pi} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 flex items-baseline justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Poll {pi + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePoll(pi)}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                  >
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  value={poll.question}
                  onChange={(e) => updatePoll(pi, { ...poll, question: e.target.value })}
                  placeholder="Poll question"
                  className={inputClass}
                />
                <div className="mt-2 space-y-2">
                  {poll.options.map((opt, oi) => {
                    if (opt._destroy) return null;
                    return (
                      <div key={oi} className="flex items-center gap-2">
                        <span className="w-6 text-center text-xs font-semibold text-gray-400">
                          {visibleOptions.indexOf(opt) + 1}.
                        </span>
                        <input
                          type="text"
                          value={opt.body}
                          onChange={(e) => updateOption(pi, oi, e.target.value)}
                          placeholder={`Option ${visibleOptions.indexOf(opt) + 1}`}
                          className={inputClass}
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(pi, oi)}
                          disabled={visibleOptions.length <= POLL_MIN_OPTIONS}
                          className="shrink-0 rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => addOption(pi)}
                  disabled={visibleOptions.length >= POLL_MAX_OPTIONS}
                  className="mt-2 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  + Add option
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <h4 className="mb-2 text-sm font-semibold text-gray-900">Free-text questions</h4>
        <div className="space-y-3">
          {section.questions.map((question, qi) => (
            <div key={qi} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Question {qi + 1}
              </span>
              <textarea
                value={question.prompt}
                onChange={(e) => updateQuestion(qi, e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="e.g. What's the biggest challenge you're facing?"
                className={`${inputClass} mt-1`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WebinarForm({
  webinar,
  statuses = ['scheduled', 'cancelled', 'completed'],
  visibilities = ['public', 'private'],
  instructors = [],
  hideStatus = false,
  submitUrl,
  method = 'post',
  submitLabel = 'Save',
  cancelHref = '/superadmin/webinars',
}) {
  const initialStart = splitStartsAt(webinar.starts_at);
  const initialCreditNum = Math.round(Number(webinar.credit_hours));
  const initialCredit = CREDIT_HOUR_OPTIONS.includes(initialCreditNum) ? initialCreditNum : 1;

  // A finished broadcast's schedule is history — the server rejects any change
  // to starts_at/ends_at once broadcast_state is "ended". credit_hours and
  // time_zone are locked with it because ends_at is derived from them here.
  const schedule_locked = webinar.broadcast_state === 'ended';
  const schedule_input_class = schedule_locked
    ? `${inputClass} bg-gray-100 text-gray-500 cursor-not-allowed`
    : inputClass;

  const [form, setForm] = useState({
    title: webinar.title || '',
    description: webinar.description || '',
    instructor_id: webinar.instructor_id ?? '',
    start_date: initialStart.date,
    start_time: initialStart.time,
    time_zone: webinar.time_zone || 'America/New_York',
    credit_hours: initialCredit,
    capacity: webinar.capacity ?? '',
    status: webinar.status || 'scheduled',
    visibility: webinar.visibility || 'public',
  });
  const [sections, setSections] = useState(() => normalizeSections(webinar.sections));
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const updateSection = (si, next) => {
    setSections(sections.map((s, i) => (i === si ? next : s)));
  };
  const addSection = () => {
    setSections([...sections, makeBlankSection(sections.length)]);
  };
  const removeSection = (si) => {
    const target = sections[si];
    if (target.id) {
      setSections(sections.map((s, i) => (i === si ? { ...s, _destroy: true } : s)));
    } else {
      setSections(sections.filter((_, i) => i !== si));
    }
  };

  const endsAt = useMemo(
    () => addHoursLocal(form.start_date, form.start_time, form.credit_hours),
    [form.start_date, form.start_time, form.credit_hours],
  );

  const visibleSectionCount = sections.filter((s) => !s._destroy).length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.start_date || !form.start_time) return;
    setSubmitting(true);
    const starts_at = `${form.start_date}T${form.start_time}`;
    const opts = { preserveScroll: true, onFinish: () => setSubmitting(false) };

    if (anySectionHasFile(sections)) {
      const fd = new FormData();
      const append = (k, v) => fd.append(`webinar[${k}]`, v == null ? '' : String(v));
      append('title', form.title);
      append('description', form.description);
      append('instructor_id', form.instructor_id);
      if (!schedule_locked) {
        append('starts_at', starts_at);
        append('ends_at', endsAt);
        append('time_zone', form.time_zone);
        append('credit_hours', String(form.credit_hours));
      }
      append('capacity', form.capacity === '' ? '' : form.capacity);
      append('status', form.status);
      append('visibility', form.visibility);
      append('delivery_mode', 'hls');
      appendSectionsToFormData(fd, sections);
      if (method === 'patch') {
        fd.append('_method', 'patch');
        router.post(submitUrl, fd, opts);
      } else {
        router.post(submitUrl, fd, opts);
      }
      return;
    }

    const schedule_payload = schedule_locked ? {} : {
      starts_at,
      ends_at: endsAt,
      time_zone: form.time_zone,
      credit_hours: String(form.credit_hours),
    };

    const payload = {
      webinar: {
        title: form.title,
        description: form.description,
        instructor_id: form.instructor_id === '' ? null : form.instructor_id,
        ...schedule_payload,
        capacity: form.capacity === '' ? null : form.capacity,
        status: form.status,
        visibility: form.visibility,
        delivery_mode: 'hls',
        sections_attributes: sectionsJsonPayload(sections),
      },
    };
    if (method === 'patch') router.patch(submitUrl, payload, opts);
    else router.post(submitUrl, payload, opts);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
      <div>
        <label htmlFor="title" className={labelClass}>Title <span className="text-rose-500">*</span></label>
        <input
          id="title"
          type="text"
          value={form.title}
          onChange={update('title')}
          required
          autoFocus
          placeholder="e.g. Dementia Care Updates"
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="description" className={labelClass}>Description</label>
        <textarea
          id="description"
          value={form.description}
          onChange={update('description')}
          rows={4}
          placeholder="What attendees will learn"
          className={inputClass}
        />
      </div>

      <div className={`grid grid-cols-1 ${hideStatus ? '' : 'sm:grid-cols-2'} gap-4`}>
        <div>
          <label htmlFor="instructor_id" className={labelClass}>Instructor</label>
          <select
            id="instructor_id"
            value={form.instructor_id ?? ''}
            onChange={update('instructor_id')}
            className={inputClass}
          >
            <option value="">— Unassigned —</option>
            {instructors.map((i) => (
              <option key={i.id} value={i.id}>{i.name} ({i.email})</option>
            ))}
          </select>
          {instructors.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">
              No instructors yet. <a href="/superadmin/instructors/new" className="text-indigo-600 hover:text-indigo-700">Add one →</a>
            </p>
          )}
        </div>
        {!hideStatus && (
          <div>
            <label htmlFor="status" className={labelClass}>Status</label>
            <select
              id="status"
              value={form.status}
              onChange={update('status')}
              className={`${inputClass} capitalize`}
            >
              {statuses.map((s) => (
                <option key={s} value={s} className="capitalize">{s}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="visibility" className={labelClass}>Visibility</label>
        <select
          id="visibility"
          value={form.visibility}
          onChange={update('visibility')}
          className={`${inputClass} capitalize`}
        >
          {visibilities.map((v) => (
            <option key={v} value={v} className="capitalize">{v}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Private webinars only appear to users you invite from this webinar's page.
        </p>
      </div>

      {schedule_locked && (
        <p data-testid="schedule-locked-notice" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This broadcast has ended, so its schedule can no longer be changed. Every
          other field is still editable.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="start_date" className={labelClass}>Start date <span className="text-rose-500">*</span></label>
          <input
            id="start_date"
            type="date"
            value={form.start_date}
            onChange={update('start_date')}
            required
            disabled={schedule_locked}
            className={schedule_input_class}
          />
        </div>
        <div>
          <label htmlFor="start_time" className={labelClass}>Start time <span className="text-rose-500">*</span></label>
          <input
            id="start_time"
            type="time"
            value={form.start_time}
            onChange={update('start_time')}
            required
            disabled={schedule_locked}
            className={schedule_input_class}
          />
        </div>
        <div>
          <label htmlFor="time_zone" className={labelClass}>Time zone <span className="text-rose-500">*</span></label>
          <select
            id="time_zone"
            value={form.time_zone}
            onChange={update('time_zone')}
            required
            disabled={schedule_locked}
            className={schedule_input_class}
          >
            {TIME_ZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="credit_hours" className={labelClass}>Credit hours <span className="text-rose-500">*</span></label>
          <select
            id="credit_hours"
            value={form.credit_hours}
            onChange={(e) => setForm({ ...form, credit_hours: Number(e.target.value) })}
            disabled={schedule_locked}
            className={schedule_input_class}
          >
            {CREDIT_HOUR_OPTIONS.map((h) => (
              <option key={h} value={h}>{h} {h === 1 ? 'hour' : 'hours'}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="capacity" className={labelClass}>
            Capacity <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="capacity"
            type="number"
            min="1"
            step="1"
            value={form.capacity ?? ''}
            onChange={update('capacity')}
            placeholder="Unlimited"
            className={inputClass}
          />
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm">
        <span className="font-semibold text-gray-700">Ends at:</span>{' '}
        <span className="text-gray-900">
          {schedule_locked ? (webinar.ends_at_display || '—') : formatPreview(endsAt)}
        </span>
        {(schedule_locked ? webinar.ends_at_display : endsAt) && (
          <span className="text-gray-500"> · {form.time_zone}</span>
        )}
        <p className="mt-0.5 text-xs text-gray-500">
          {schedule_locked
            ? 'Recorded end time for this completed broadcast.'
            : 'Calculated from start time + credit hours, in the selected time zone.'}
        </p>
      </div>

      <div className="border-t border-gray-200 pt-5">
        <div className="mb-3 flex items-baseline justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Sessions</h3>
            <p className="text-xs text-gray-500">
              Split the webinar into hourly sessions. Each session has its own PDF,
              polls, and questions. You'll switch between sessions on the broadcast
              page as the day progresses.
            </p>
          </div>
          <button
            type="button"
            onClick={addSection}
            className="shrink-0 rounded-md border border-dashed border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
          >
            + Add session
          </button>
        </div>
        <div className="space-y-4">
          {sections.map((section, si) => (
            section._destroy && !section.id ? null : (
              <SectionEditor
                key={section.id ?? `new-${si}`}
                section={section}
                si={si}
                updateSection={updateSection}
                removeSection={removeSection}
                canRemove={visibleSectionCount > 1}
              />
            )
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
        <Link
          href={cancelHref}
          className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
